<script setup lang="ts">
import { computed, ref } from 'vue';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import PersonalNewsCountdown from '@/components/common/personal_news/personal-news-countdown.vue';
import {
  createPersonalNewsHomeShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { usePersonalFeedSummary } from '@/components/feed-summary/composables/use-personal-feed-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import PersonalNewsArticleList from '@/components/home/personal-news-article-list.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import {
  executionInformationForDate,
  getMorningFeedData,
  HOME_ALL_MEDIA_VISIBLE_ARTICLE_COUNT,
  HOME_VISIBLE_ARTICLE_COUNT,
  isAfternoonFeedEnabled,
  latestPersonalFeedDatetime,
} from '@/utils/personalNews';
import {
  STATES,
  usePersonalNewsMultipleExecution,
  useUserInfo,
} from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const layoutVariant = 'legacy';

const store = useStore();
const emitter = useEmitter();
const { data: userInfo, mutate } = useUserInfo();
emitter.on('article-updated', () => {
  mutate();
});

const feedType = computed(() => store.state.feedType.feedType);

const feedCount = computed(() =>
  feedType.value === 'all'
    ? HOME_ALL_MEDIA_VISIBLE_ARTICLE_COUNT
    : HOME_VISIBLE_ARTICLE_COUNT,
);

const sentFirstPersonalNewsFeedAt = computed(() =>
  userInfo.value?.sent_first_personal_news_feed_at
    ? dayjs(userInfo.value?.sent_first_personal_news_feed_at)
    : undefined,
);

const feedLastUpdatedAt = computed(() =>
  latestPersonalFeedDatetime(sentFirstPersonalNewsFeedAt.value),
);

const hasPersonalNews = computed(
  () => userInfo.value?.has_personal_news ?? false,
);

const morningFeedData = computed(() =>
  getMorningFeedData(
    feedLastUpdatedAt.value,
    sentFirstPersonalNewsFeedAt.value,
  ),
);

const { data: domesticFeedMorning, state: domesticStateMorning } =
  usePersonalNewsMultipleExecution(
    feedType,
    'domestic',
    morningFeedData.value.date,
    morningFeedData.value.execution,
    ref(1),
    feedCount,
  );
const { data: foreignFeedMorning, state: foreignStateMorning } =
  usePersonalNewsMultipleExecution(
    feedType,
    'foreign',
    morningFeedData.value.date,
    morningFeedData.value.execution,
    ref(1),
    feedCount,
  );
const { data: domesticFeedAfternoon, state: domesticStateAfternoon } =
  usePersonalNewsMultipleExecution(
    feedType,
    'domestic',
    feedLastUpdatedAt.value,
    'afternoon',
    ref(1),
    feedCount,
  );
const { data: foreignFeedAfternoon, state: foreignStateAfternoon } =
  usePersonalNewsMultipleExecution(
    feedType,
    'foreign',
    feedLastUpdatedAt.value,
    'afternoon',
    ref(1),
    feedCount,
  );

const isLoadedMorning = computed(() => {
  if (feedType.value === 'all') {
    return (
      domesticStateMorning.value === STATES.SUCCESS &&
      foreignStateMorning.value === STATES.SUCCESS
    );
  } else if (feedType.value === 'domestic') {
    return domesticStateMorning.value === STATES.SUCCESS;
  } else {
    return foreignStateMorning.value === STATES.SUCCESS;
  }
});

const isLoadedAfternoon = computed(() => {
  if (feedType.value === 'all') {
    return (
      domesticStateAfternoon.value === STATES.SUCCESS &&
      foreignStateAfternoon.value === STATES.SUCCESS
    );
  } else if (feedType.value === 'domestic') {
    return domesticStateAfternoon.value === STATES.SUCCESS;
  } else {
    return foreignStateAfternoon.value === STATES.SUCCESS;
  }
});

const isLoaded = computed(
  () =>
    isLoadedMorning.value &&
    (!isAfternoonFeedEnabled(feedLastUpdatedAt.value) ||
      isLoadedAfternoon.value),
);

const executionInfoMorning = computed(() =>
  executionInformationForDate(
    feedLastUpdatedAt.value,
    'morning',
    sentFirstPersonalNewsFeedAt.value,
  ),
);
const executionInfoAfternoon = computed(() =>
  executionInformationForDate(
    feedLastUpdatedAt.value,
    'afternoon',
    sentFirstPersonalNewsFeedAt.value,
  ),
);

const articleCount = computed(() => {
  return (
    (domesticFeedMorning.value?.count ?? 0) +
    (foreignFeedMorning.value?.count ?? 0) +
    (domesticFeedAfternoon.value?.count ?? 0) +
    (foreignFeedAfternoon.value?.count ?? 0)
  );
});

const visibleArticleCount = computed(() => {
  return (
    (domesticFeedMorning.value?.personal_feeds.length ?? 0) +
    (foreignFeedMorning.value?.personal_feeds.length ?? 0) +
    (domesticFeedAfternoon.value?.personal_feeds.length ?? 0) +
    (foreignFeedAfternoon.value?.personal_feeds.length ?? 0)
  );
});

const { setRef, requestId } = useHomeSectionViewTracking(
  'personal_feed_view',
  isLoaded,
  () => ({
    document_count: visibleArticleCount.value,
    layout_variant: layoutVariant,
  }),
);

const {
  summary,
  generateButtonState,
  generateSummary,
  shouldShowSummary,
  summarySuffix,
} = usePersonalFeedSummary(
  {
    page_type: 'feed',
    feed_type: feedType.value,
    doc_type: 'article',
    date: feedLastUpdatedAt.value.format('YYYY-MM-DD'),
    execution: isAfternoonFeedEnabled(feedLastUpdatedAt.value)
      ? 'afternoon'
      : 'morning',
  },
  articleCount,
  'home',
);

useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  'personal_feed' as const,
  createPersonalNewsHomeShouldEnable(),
);
</script>

<template>
  <div class="personal-feed" v-if="hasPersonalNews" :ref="setRef">
    <div class="title c-title-bold--xm">
      パーソナルニュース
      <FeedSummaryGenerationButton
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <FeedSummary
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      :suffix="summarySuffix"
      class="feed-summary"
      feature="personal_feed"
      page-name="home"
    />
    <template
      v-if="
        (isAfternoonFeedEnabled(feedLastUpdatedAt) ||
          executionInfoAfternoon.isFirstExecution) &&
        !executionInfoMorning.isFirstExecution
      "
    >
      <PersonalNewsArticleList
        :execution-info="executionInfoAfternoon"
        :domestic-articles="domesticFeedAfternoon?.personal_feeds ?? []"
        :foreign-articles="foreignFeedAfternoon?.personal_feeds ?? []"
        :previous-total-feed-count="0"
        :is-loaded="isLoadedAfternoon"
        :contents-context="{
          event_name: 'personal_feed',
          parent_request_id: requestId,
        }"
      />
      <div class="spacing-24"></div>
    </template>
    <template v-if="!executionInfoAfternoon.isFirstExecution">
      <PersonalNewsArticleList
        :execution-info="executionInfoMorning"
        :domestic-articles="domesticFeedMorning?.personal_feeds ?? []"
        :foreign-articles="foreignFeedMorning?.personal_feeds ?? []"
        :previous-total-feed-count="0"
        :is-loaded="isLoadedMorning"
        :contents-context="{
          event_name: 'personal_feed',
          parent_request_id: requestId,
        }"
      />
    </template>
  </div>
  <div class="no-articles" v-else-if="userInfo" :ref="setRef">
    <PersonalNewsCountdown
      v-if="userInfo.personal_news_counts"
      :actioned-article-counts="
        userInfo.personal_news_counts ?? { domestic: 0, foreign: 0 }
      "
    ></PersonalNewsCountdown>
    <div class="loading-container" v-else>
      <DgrLoading class="loading" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.personal-feed {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 20px 24px;

  .title {
    display: flex;
    align-items: center;
    margin-bottom: 4px;

    .generation-button {
      margin-left: auto;
    }
  }
  .subtitle {
    color: $color-gray800;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 14px 48px 20px 48px;
  min-height: 300px;
  .loading {
    margin: 0 auto;
  }
}

.feed-summary {
  margin: 8px 0;
}
</style>
